import { AddRounded, HowToVoteOutlined } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Accordion, AccordionSkeleton, ViewList } from "..";
import { IssueDto, ProposalDto, useDeleteProposal } from "../../http";
import { useDestructiveConfirm } from "../../utilities";
import { CreateProposalModal } from "./CreateProposalModal";
import { EditProposalModal } from "./EditProposalModal";
import { Proposal } from "./Proposal";

interface IProps {
  issue?: IssueDto;
  isLoading: boolean;
}

const IssueProposalsList = (props: IProps) => {
  const confirm = useDestructiveConfirm();
  const { mutate: deleteProposal } = useDeleteProposal();
  const [isExpanded, setIsExpanded] = useState(props.issue && props.issue.proposals.length > 0);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingProposal, setEditingProposal] = useState<ProposalDto | null>(null);

  const [prevIssue, setPrevIssue] = useState<IssueDto | undefined>(props.issue);

  if (props.issue && props.issue.guid !== prevIssue?.guid) {
    setPrevIssue(props.issue);
    setIsExpanded(props.issue.proposals.length > 0);
  }

  function handleAddClicked() {
    setIsCreateModalVisible(true);
  }

  function handleEditClicked(proposal: ProposalDto) {
    setEditingProposal(proposal);
    setIsEditModalVisible(true);
  }

  function handleProposalUpdated() {
    setIsEditModalVisible(false);
    setEditingProposal(null);
  }

  async function handleDeleteClicked(proposal: ProposalDto) {
    if (!props.issue) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Delete Proposal",
      content: "Are you sure you want to delete this proposal?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteProposal({
      proposalGuid: proposal.guid,
      issueGuid: props.issue.guid,
      issueCreatedByUser: props.issue.createdByUser,
      teamSlug: props.issue.assignedTeam?.slug,
    });
  }

  return props.isLoading || props.issue === undefined ? (
    <AccordionSkeleton />
  ) : (
    <>
      <Accordion
        title={(expanded) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <HowToVoteOutlined sx={{ color: "text.secondary" }} />
            <Stack direction="row" spacing={0.25}>
              <Typography variant="label">Proposals</Typography>
              <Typography variant="caption">{expanded ? undefined : `(${props.issue!.proposals.length})`}</Typography>
            </Stack>
          </Stack>
        )}
        expanded={isExpanded}
        onExpandClicked={() => setIsExpanded(!isExpanded)}
        hoverActions={
          <IconButton size="small" onClick={handleAddClicked}>
            <AddRounded />
          </IconButton>
        }
        hoverActionsAlwaysVisible
        accordionDetailsSx={{ px: 0.5, pb: 0.5 }}
      >
        <ViewList
          dataSource={props.issue?.proposals}
          isLoading={props.isLoading}
          spacing={0.5}
          renderItem={(proposal: ProposalDto) => (
            <Proposal
              key={proposal.guid}
              issue={props.issue!}
              proposal={proposal}
              onEditClicked={handleEditClicked}
              onDeleteClicked={handleDeleteClicked}
            />
          )}
          placeholder={"No proposals yet."}
        />
      </Accordion>

      {props.issue && (
        <>
          <CreateProposalModal
            visible={isCreateModalVisible}
            issue={props.issue}
            onCreated={() => {
              setIsCreateModalVisible(false);
              setIsExpanded(true);
            }}
            onCancelled={() => setIsCreateModalVisible(false)}
          />

          {editingProposal && (
            <EditProposalModal
              visible={isEditModalVisible}
              proposal={editingProposal}
              issueGuid={props.issue.guid}
              onUpdated={handleProposalUpdated}
              onCancelled={() => setIsEditModalVisible(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export { IssueProposalsList };
